import PropTypes from "prop-types"
import React, { useContext, useState } from "react"

import Button from "../../../../components/Button"
import { SignContractWizardContext } from "../SignContractWizardContext"
import RequiredFormInput, { recursivelySet } from "./RequiredFormInput"

const InsuranceAndRegistration = ({ form }) => {
  const { state, dispatch, quote } = useContext(SignContractWizardContext)
  const { requiredInformation } = state

  const {
    insuranceRequired,
    registrationRequired,
    phoneInvalid,
    insuranceRequirement,
    registrationRequirement,
  } = quote

  const insuranceDocumentUploadRequired =
    insuranceRequired && insuranceRequirement === "info_and_upload"
  const registrationDocumentUploadRequired =
    registrationRequired && registrationRequirement === "info_and_upload"

  return (
    <div className="flex flex-col space-y-6">
      {phoneInvalid && (
        <Contact
          phone={requiredInformation.phone}
          dispatch={dispatch}
          form={form}
        />
      )}
      {insuranceRequired && (
        <Insurance
          insurance={requiredInformation.insurance}
          dispatch={dispatch}
          form={form}
          documentUploadRequired={insuranceDocumentUploadRequired}
        />
      )}
      {registrationRequired && (
        <Registration
          registration={requiredInformation.registration}
          dispatch={dispatch}
          form={form}
          documentUploadRequired={registrationDocumentUploadRequired}
        />
      )}
    </div>
  )
}

const Contact = ({ phone, form, dispatch }) => {
  return (
    <div id="contact" role="region" aria-label="Contact">
      <h4 className="mb-4">Contact Info</h4>
      <RequiredFormInput
        inputKey="phone"
        label="Phone"
        value={phone}
        form={form}
      />
    </div>
  )
}

Contact.propTypes = {
  phone: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
}

const Insurance = ({ insurance, form, documentUploadRequired }) => {
  return (
    <div id="insurance" role="region" aria-label="Insurance">
      <h4>Insurance</h4>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <RequiredFormInput
          inputKey="insurance.policyNumber"
          label={"Policy Number"}
          value={insurance.policyNumber}
          form={form}
          type="text"
          alphanumeric={true}
        />
        <RequiredFormInput
          inputKey="insurance.companyName"
          label={"Company Name"}
          value={insurance.companyName}
          form={form}
          type="text"
          alphanumeric={true}
        />
        <RequiredFormInput
          inputKey="insurance.expirationDate"
          label={"Expiration Date"}
          value={insurance.expirationDate}
          form={form}
          type="date"
        />
      </div>
      {documentUploadRequired && (
        <DocumentUpload
          title="Upload Insurance Document"
          inputKey="insurance.document"
          form={form}
        />
      )}
    </div>
  )
}

Insurance.propTypes = {
  insurance: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  documentUploadRequired: PropTypes.bool.isRequired,
}

const Registration = ({ registration, form, documentUploadRequired }) => {
  return (
    <div id="registration" role="region" aria-label="Registration">
      <h4>Registration</h4>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <RequiredFormInput
          inputKey="registration.registrationNumber"
          label={"Registration Number"}
          value={registration.registrationNumber}
          form={form}
          type="text"
          alphanumeric={true}
        />
        <RequiredFormInput
          inputKey="registration.expirationDate"
          label={"Expiration Date"}
          value={registration.expirationDate}
          form={form}
          type="date"
        />
      </div>
      {documentUploadRequired && (
        <DocumentUpload
          title="Upload Registration Document"
          inputKey="registration.document"
          form={form}
        />
      )}
    </div>
  )
}

Registration.propTypes = {
  registration: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  documentUploadRequired: PropTypes.bool.isRequired,
}

const DocumentUpload = ({ title, inputKey, form }) => {
  const { dispatch, state } = useContext(SignContractWizardContext)

  const { register, watch, resetField } = form
  const { onChange, onBlur, name, ref } = register(inputKey)
  const inputFileList = watch(inputKey)

  // Get existing document from state
  const documentType = inputKey.includes("insurance")
    ? "insurance"
    : "registration"
  const existingDocument =
    state.requiredInformation[documentType]?.existingDocument

  let file
  const fileSelected = inputFileList !== undefined && inputFileList.length === 1
  if (fileSelected) {
    file = inputFileList[0]
  }

  // Track if user wants to replace existing document
  const [replacingDocument, setReplacingDocument] = useState(false)

  const handleFileSelected = (event) => {
    onChange(event)
    const payload = recursivelySet(inputKey, true)
    dispatch({ type: "REQUIRED_INFORMATION_CHANGED", payload })
  }

  const handleFileReplace = () => {
    resetField(inputKey)
    const payload = recursivelySet(inputKey, false)
    dispatch({ type: "REQUIRED_INFORMATION_CHANGED", payload })
    setReplacingDocument(false)
  }

  const handleReplaceExisting = () => {
    setReplacingDocument(true)
  }

  const handleCancelReplace = () => {
    setReplacingDocument(false)
    resetField(inputKey)
  }

  // If there's an existing document and we're not replacing it, show the existing document UI
  if (existingDocument && !replacingDocument && !fileSelected) {
    return (
      <div className="border-green-300 bg-green-50 flex cursor-default flex-col items-start space-y-3 rounded-lg border p-3">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center">
            <i className="fa-solid fa-check mr-2 text-teal-600" />
            <span className="font-medium text-gray-700">Document Attached</span>
          </div>
        </div>

        {existingDocument.previewUrl && (
          <div className="flex items-center space-x-4">
            <img
              src={existingDocument.previewUrl}
              alt="Document preview"
              className="h-32 w-32 rounded border border-gray-300 object-center"
            />
          </div>
        )}
        <div className="flex space-x-2">
          <a
            href={existingDocument.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-sm"
          >
            View
          </a>
          <Button small onClick={handleReplaceExisting} variant="secondary">
            Replace Document
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex cursor-default flex-col items-start space-y-3 rounded-lg border border-gray-300 p-3">
      <label htmlFor={`${title}-file`} className="mb-0 flex">
        {replacingDocument ? `Replace ${title}` : title}
        <span className="ml-0.5 text-red-600">*</span>
      </label>

      {fileSelected ? (
        <div className="flex items-center">
          <span className="mr-2 text-gray-600">{file?.name}</span>
          <Button
            small
            icon="fa-sf-trashcan"
            onClick={handleFileReplace}
            variant="ghost"
            iconOnly
          />
        </div>
      ) : (
        <>
          <input
            id={`${title}-file`}
            type="file"
            required={!existingDocument}
            onChange={handleFileSelected}
            onBlur={onBlur}
            name={name}
            ref={ref}
            accept={"image/jpg, image/jpeg, image/png, application/pdf"}
          />
          <span className="text-gray-600">
            PDF and image files accepted (pdf, png, or jpg).
          </span>

          {replacingDocument && (
            <Button small onClick={handleCancelReplace} variant="ghost">
              Cancel
            </Button>
          )}
        </>
      )}
    </div>
  )
}

DocumentUpload.propTypes = {
  title: PropTypes.string.isRequired,
  inputKey: PropTypes.string.isRequired,
  file: PropTypes.shape({
    content: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
  }),
  form: PropTypes.object.isRequired,
}

InsuranceAndRegistration.propTypes = {
  form: PropTypes.object.isRequired,
}

export default InsuranceAndRegistration
